import React, { ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import GenericRowWrapper from './GenericRowWrapper';
import Row from '../connectedForm/Row';
import Fieldset from '../connectedForm/Fieldset';
import Legend from '../connectedForm/Legend';
import { AutoSizer, List } from 'react-virtualized';
import styled from 'styled-components';
import { TabContainer } from './TabContent';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import TabContext from '../Context/TabContext';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';

const setColor = ({ rowData, selected, theme, hasErrors }) => {
  let border = '3px solid transparent';
  if ((rowData && rowData.deleted) || hasErrors) {
    border = `3px solid ${theme.colors.inputBorderError}`;
  }
  if (selected) {
    border = `3px solid ${theme.colors.brandAccent}`;
  }
  return `
    background-color: ${p4Tokens.colorNeutralBackground1};
    padding: 2px 5px;
    border-left: ${border};
    color: ${p4Tokens.colorNeutralForeground1};
    border-bottom: 1px solid ${theme.colors.inputBorder};
  `;
};

const ListRow: (props: any) => ReactElement = styled.div`
  ${setColor};
  &:hover {
    background-color: ${p4Tokens.colorNeutralBackground5};
  }
`;

export const GenericArrayCard = styled.div`
  cursor: pointer;
  width: 100%;
  height: 57px;
`;
export const GenericArrayCardTitle = styled.div`
  font-size: 15px;
  text-transform: uppercase;
`;
export const GenericArrayCardSubtitle = styled.div`
  font-size: 0.825rem;
  font-weight: 300;
  line-height: 95%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3; /* number of lines to show */
  line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const GenericArrayContext = React.createContext<{
  selectedKey: number;
  setSelectedKey: (value: ((prevState: number) => number) | number) => void;
  value: Array<any>;
  renderRow: any;
  renderRowIsComponent: boolean;
}>({});

const GenericRowStyled = ({ index, style }) => {
  const { selectedKey, setSelectedKey, value, renderRow, renderRowIsComponent } = useContext(GenericArrayContext);
  const connectedForm = useContext(ConnectedFormContext);

  const rowData = Array.isArray(value) && index < value.length ? value[index] : null;
  const selected = selectedKey === index;

  const hasErrors = useMemo(() => {
    const { validationErrors, registeredFields } = connectedForm;

    let rowHasErrors = false;
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      const fields = Object.keys(validationErrors);
      fields.forEach((field) => {
        const matched = field?.match(/\d+/);
        if (matched?.[0] === index.toString() && registeredFields?.[field]) {
          rowHasErrors = true;
        }
      });
    }
    return rowHasErrors;
  }, [connectedForm, index]);

  const onClick = () => {
    setSelectedKey(index);
  };

  const RowComponent = renderRow;

  return (
    <ListRow onClick={onClick} style={style} key={index} rowData={rowData} selected={selected} hasErrors={hasErrors}>
      {renderRowIsComponent ? (
        <RowComponent value={rowData} rowData={rowData} rowIndex={index} />
      ) : (
        <GenericRowWrapper value={rowData} renderRow={renderRow} index={index} />
      )}
    </ListRow>
  );
};

const GenericArrayList = ({
  header,
  value: propsValue,
  enableNewRows,
  renderRow,
}: {
  header: ReactElement;
  value: Array<any>;
  enableNewRows: boolean;
  renderRow: (index) => ReactElement;
}) => {
  const value = propsValue || [];

  //Tag an additional empty element to the end of values for new row.
  const elements: Array<ReactElement> = [];
  for (let i = 0; i < (enableNewRows ? value.length + 1 : value.length); i++) {
    elements.push(renderRow(i));
  }

  return (
    <div>
      {header}
      {elements}
    </div>
  );
};

const GenericArrayListDetail = ({
  header,
  value: propsValue,
  renderDetails,
  renderRowIsComponent,
  detailProps,
  labelCols,
  inputCols,
  disableHeight,
  rowHeight,
  enableNewRows,
  selectedKey,
}: {
  header: ReactElement;
  value: any;
  renderDetails: (props: any) => ReactElement;
  renderRowIsComponent: boolean;
  detailProps: any;
  labelCols: number;
  inputCols: number;
  disableHeight: boolean;
  rowHeight: number;
  enableNewRows: boolean;
  selectedKey;
}) => {
  const value = useMemo(() => propsValue ?? [], [propsValue]);

  const detailSections: Array<ReactElement> = [];
  for (let i = 0; i <= value.length; i++) {
    const rowValue = i < value.length ? value[i] : {};
    const Details = renderDetails!;
    const isActiveTab = selectedKey === i;
    if (!isActiveTab) continue;
    const rowDetail = renderRowIsComponent ? (
      <TabContainer key={`_${i}`} isActiveTab={isActiveTab}>
        <TabContext.Provider value={{ isActiveTab: isActiveTab }}>
          <Details value={rowValue} rowData={rowValue} rowIndex={i} {...detailProps} />
        </TabContext.Provider>
      </TabContainer>
    ) : (
      <TabContainer key={`_${i}`} isActiveTab={isActiveTab}>
        <TabContext.Provider value={{ isActiveTab: isActiveTab }}>
          <GenericRowWrapper
            value={rowValue}
            index={i}
            renderRow={Details}
            renderRowIsComponent={renderRowIsComponent}
            rowProps={detailProps}
          />
        </TabContext.Provider>
      </TabContainer>
    );
    detailSections.push(rowDetail);
  }

  const renderRowStyled = useCallback(({ index, style }) => {
    return <GenericRowStyled index={index} style={style} />;
  }, []);

  const list = useCallback(
    ({ height, width }: { height: number; width: number }) => {
      return (
        <List
          height={height > 0 ? height : window.innerHeight - 100}
          overscanRowCount={1}
          noRowsRenderer={renderRowStyled}
          rowCount={value.length + (enableNewRows ? 1 : 0)}
          rowHeight={rowHeight}
          rowRenderer={renderRowStyled}
          width={width}
          value={value}
        />
      );
    },
    [enableNewRows, rowHeight, value, renderRowStyled]
  );

  return (
    <div>
      {header}
      <Row>
        <Fieldset cols={labelCols}>
          <AutoSizer disableHeight={disableHeight}>{list}</AutoSizer>
        </Fieldset>
        <Fieldset cols={inputCols}>{detailSections}</Fieldset>
      </Row>
    </div>
  );
};

const GenericArrayScrollableList = ({
  header,
  value,
  enableNewRows,
  rowHeight,
  renderRow,
}: {
  header: ReactElement;
  value: Array<any>;
  enableNewRows: boolean;
  rowHeight: number;
  renderRow: (index: number) => ReactElement;
}) => {
  if (!props.value) return null;
  return (
    <div>
      {header}
      <AutoSizer disableHeight>
        {({ width }) => {
          return (
            <List
              height={500}
              overscanRowCount={1}
              noRowsRenderer={() => {
                return <div>No Contacts Found</div>;
              }}
              rowCount={value.length + (enableNewRows ? 1 : 0)}
              rowHeight={rowHeight}
              rowRenderer={({ index }) => {
                return renderRow(index);
              }}
              width={width}
            />
          );
        }}
      </AutoSizer>
    </div>
  );
};

type IGenericArrayProps = {
  active: boolean;
  clearButton: boolean;
  mandatory: boolean;
  includeLabel: boolean;
  inputPrefix: string;
  readOnly: boolean;
  flexBox: boolean;
  labelCols: number;
  inputCols: number;
  id: string;
  hint: string;
  value: any;
  validationErrors: Array<any>;
  showWhenNotActive: boolean;
  label: string;
  type: string;
  renderRowIsComponent?: boolean;
  renderRow: (props: { value: any; rowData: any; rowIndex: number }) => ReactElement;
  renderDetails?: (props: any) => ReactElement;
  renderHeader?: () => ReactElement;
  defaultValue: any;
  scrollable: boolean;
  sortField: string;
  rowHeight: number;
  disableHeight?: boolean;
  enableNewRows?: boolean;
  detailProps?: any;
  title?: string;
};

export const GenericArray = (props: IGenericArrayProps) => {
  const [selectedKey, setSelectedKey] = useState(0);

  const genericArrayContext = useMemo(() => {
    return {
      selectedKey,
      setSelectedKey,
      renderRow: props.renderRow,
      renderRowIsComponent: props.renderRowIsComponent,
      value: props.value,
    };
  }, [props.renderRow, props.renderRowIsComponent, props.value, selectedKey]);

  const renderRow = useCallback(
    (rowKey) => {
      const value = props.value || [];
      const rowData = rowKey < value.length ? value[rowKey] : {};

      if (props.renderRowIsComponent) {
        const RowComponent = props.renderRow;
        return <RowComponent value={rowData} rowData={rowData} key={`_${rowKey}`} rowIndex={rowKey} />;
      }

      return <GenericRowWrapper value={rowData} key={`_${rowKey}`} index={rowKey} renderRow={props.renderRow} />;
    },
    [props.renderRow, props.renderRowIsComponent, props.value]
  );

  let header: ReactElement | null = null;
  if (!props.renderHeader && props.title) {
    header = <Legend label={props.title} />;
  } else if (props.renderHeader) {
    header = props.renderHeader();
  }

  let content;
  if (props.renderDetails) {
    content = (
      <GenericArrayListDetail
        header={header}
        value={props.value}
        detailProps={props.detailProps}
        inputCols={props.inputCols}
        labelCols={props.labelCols}
        renderRowIsComponent={props.renderRowIsComponent}
        renderDetails={props.renderDetails}
        disableHeight={props.disableHeight}
        rowHeight={props.rowHeight}
        enableNewRows={props.enableNewRows}
        selectedKey={selectedKey}
      />
    );
  } else if (props.scrollable) {
    content = (
      <GenericArrayScrollableList
        header={header}
        renderRow={renderRow}
        value={props.value}
        enableNewRows={props.enablenewRows}
        rowHeight={props.rowHeight}
      />
    );
  } else {
    content = (
      <GenericArrayList header={header} renderRow={renderRow} value={props.value} enableNewRows={props.enableNewRows} />
    );
  }

  return <GenericArrayContext.Provider value={genericArrayContext}>{content}</GenericArrayContext.Provider>;
};

GenericArray.defaultProps = {
  active: false,
  mandatory: false,
  clearButton: false,
  placeholder: '',
  includeLabel: true,
  inputPrefix: null,
  readOnly: false,
  flexBox: true,
  labelCols: 1 / 4,
  inputCols: 3 / 4,
  value: [],
  validationErrors: [],
  hint: '',
  showWhenNotActive: false,
  type: 'text',
  label: '',
  id: '',
  renderDetails: null,
  defaultValue: {},
  scrollable: false,
  rowHeight: 55,
  detailProps: {},
  disableHeight: false,
};

export default GenericArray;
